import { useState } from "react";
import { useI18n } from "./i18n";
import type { HudSnapshot, GameFacade, BuildMode } from "./GameFacade";

interface BuildPanelProps {
  snapshot: HudSnapshot;
  facade: GameFacade;
  /** 拖拽开始时回调，支持塔和调试怪物。 */
  onDragStartBuild?: (mode: BuildMode) => void;
  /** 拖拽结束时回调（取消或完成）。 */
  onDragEnd?: () => void;
}

/**
 * Left-side build panel: pick a tower type, then drag it onto the map to place it.
 * Also lets the player select an existing tower to upgrade/sell.
 */
export function BuildPanel({ snapshot, facade, onDragStartBuild, onDragEnd }: BuildPanelProps) {
  const [activeBuildType, setActiveBuildType] = useState<BuildMode | null>(null);
  const { t, tEntity } = useI18n();

  function pickTower(type: string) {
    if (!snapshot.unlimitedGold && snapshot.gold < towerCost(type)) {
      setActiveBuildType(null);
      return;
    }
    setActiveBuildType({ kind: "tower", type });
    facade.setBuildMode({ kind: "tower", type });
    facade.selectTower(null);
  }

  function cancelBuild() {
    setActiveBuildType(null);
    facade.setBuildMode(null);
  }

  function towerCost(type: string): number {
    return snapshot.towerTypes.find((tt) => tt.id === type)?.cost ?? 0;
  }

  function handleDragStart(e: React.DragEvent<HTMLButtonElement>, type: string) {
    e.dataTransfer.setData("text/plain", JSON.stringify({ kind: "tower", type }));
    e.dataTransfer.effectAllowed = "move";
    onDragStartBuild?.({ kind: "tower", type });
    // 设置自定义拖拽幽灵
    const ghost = document.createElement("div");
    ghost.style.cssText =
      "position:absolute;top:-9999px;width:40px;height:40px;border-radius:50%;background:rgba(96,165,250,0.6);border:2px solid #60a5fa;";
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 20, 20);
    setTimeout(() => {
      if (document.body.contains(ghost)) document.body.removeChild(ghost);
    }, 0);
  }

  return (
    <aside className="build-panel" data-testid="build-panel">
      <section className="panel-section">
        <h3 className="panel-title">{t("build_panel_title")}</h3>
        {snapshot.towerTypes.map((tower) => {
          const affordable = snapshot.unlimitedGold || snapshot.gold >= tower.cost;
          const active = activeBuildType?.kind === "tower" && activeBuildType.type === tower.id;
          return (
            <button
              key={tower.id}
              className={`tower-card ${active ? "active" : ""} ${affordable ? "" : "disabled"}`}
              data-testid={`build-${tower.id}`}
              onClick={() => (active ? cancelBuild() : pickTower(tower.id))}
              disabled={!affordable && !active}
              draggable={affordable}
              onDragStart={(e) => handleDragStart(e, tower.id)}
              onDragEnd={onDragEnd}
            >
              {tower.skinUrl ? (
                <img
                  src={tower.skinUrl}
                  alt={tEntity("tower", tower.id)}
                  className="tower-card-skin"
                />
              ) : (
                <span className="tower-card-name">{tEntity("tower", tower.id)}</span>
              )}
              <span className="tower-card-cost">🪙 {tower.cost}</span>
            </button>
          );
        })}
        {snapshot.isDebug &&
          snapshot.enemyTypes.map((enemy) => {
            const mode: BuildMode = { kind: "enemy", type: enemy.id };
            const active = activeBuildType?.kind === "enemy" && activeBuildType.type === enemy.id;
            return (
              <button
                key={enemy.id}
                className={`tower-card ${active ? "active" : ""}`}
                data-testid={`build-enemy-${enemy.id}`}
                onClick={() => {
                  if (active) cancelBuild();
                  else {
                    setActiveBuildType(mode);
                    facade.setBuildMode(mode);
                    facade.selectEnemy(null);
                  }
                }}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("text/plain", JSON.stringify(mode));
                  e.dataTransfer.effectAllowed = "move";
                  onDragStartBuild?.(mode);
                }}
                onDragEnd={onDragEnd}
              >
                {enemy.skinUrl ? (
                  <img
                    src={enemy.skinUrl}
                    alt={tEntity("enemy", enemy.id)}
                    className="tower-card-skin"
                  />
                ) : (
                  <span className="tower-card-name">
                    {enemy.isBoss ? "👑 " : "👾 "}
                    {tEntity(enemy.isBoss ? "boss" : "enemy", enemy.id)}
                  </span>
                )}
                <span className="tower-card-cost">FREE</span>
              </button>
            );
          })}
        {activeBuildType && (
          <p className="panel-hint">{t("build_click_hint", { name: activeBuildType.type })}</p>
        )}
      </section>
    </aside>
  );
}
